/**
 * Standardised toggle switch component.
 * Colours are driven by semantic theme tokens (--token-toggle-on / --token-toggle-off)
 * so they update automatically when branding is changed in Settings.
 *
 * size="md" (default) — h-4 w-7, thumb h-3 w-3
 * size="sm"           — h-3.5 w-6, thumb h-2.5 w-2.5 (for sub-options)
 */

export default function Toggle({ checked, onChange, disabled, title, size = 'md', className = '', ariaLabel }) {
  const dimensions = size === 'sm'
    ? { width: 24, height: 14, thumb: 10, offset: 2 }
    : { width: 28, height: 16, thumb: 12, offset: 2 };
  const travel = dimensions.width - dimensions.thumb - dimensions.offset * 2;
  const trackColor = checked
    ? "var(--token-toggle-on, var(--token-primary, #1946FA))"
    : "var(--token-toggle-track-off, var(--token-toggle-off, #d1d5db))";
  const trackBorderColor = checked
    ? "var(--token-toggle-on-border, transparent)"
    : "var(--token-toggle-track-off-border, transparent)";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={disabled ? undefined : onChange}
      disabled={disabled}
      title={title}
      style={{
        width: dimensions.width,
        height: dimensions.height,
        backgroundColor: trackColor,
        border: `1px solid ${trackBorderColor}`,
      }}
      className={`
        relative inline-flex shrink-0 items-center rounded-full p-0 align-middle
        transition-[background-color,border-color,transform] duration-180 ease-out active:scale-[0.97]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1
        focus-visible:ring-[var(--token-toggle-on,#1946FA)]
        disabled:opacity-50 disabled:cursor-not-allowed
        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `.trim()}
    >
      <span
        style={{
          width: dimensions.thumb,
          height: dimensions.thumb,
          transform: `translate(${checked ? travel : 0}px, -50%)`,
          left: dimensions.offset,
          top: "50%",
          willChange: "transform",
        }}
        className="pointer-events-none absolute rounded-full bg-white shadow transition-transform duration-180 ease-out"
      />
    </button>
  )
}
